import { API_TAGS } from '../../constants/api';
import { API_CORE_URL } from '../../constants/env';
import { apiSlice } from '../apiSlice';
import { MESSAGES } from './nlpPipelinesMessages';

export const nlpPipelinesSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    fetchNlpPipelines: builder.query({
      query: ({ orgId, tenantId, urlParams = {} }) => ({
        url: `organizations/${orgId}/tenants/${tenantId}/nlp-pipelines`,
        method: 'GET',
        params: {
          pageSize: 100,
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_NLP_PIPELINES.ERROR
        }
      }),
      providesTags: (result, error, args) => {
        if (error?.error) {
          return [{ type: API_TAGS.NLP_PIPELINES, id: 'LIST' }];
        }
        return [
          ...result.items.map(({ id }) => ({ type: API_TAGS.NLP_PIPELINES, id })),
          { type: API_TAGS.NLP_PIPELINES, id: 'LIST' }
        ];
      }
    }),

    fetchNlpPipeline: builder.query({
      query: ({ orgId, tenantId, nlpPipelineId, urlParams = {} }) => ({
        url: `organizations/${orgId}/tenants/${tenantId}/nlp-pipelines/${nlpPipelineId}`,
        method: 'GET',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.FETCH_NLP_PIPELINE.ERROR
        }
      }),
      providesTags: (result, error, args) => {
        return [
          { type: API_TAGS.NLP_PIPELINES, id: args.nlpPipelineId }
        ];
      }
    }),

    fetchNlpPipelineStatus: builder.query({
      query: ({ orgId, tenantId, nlpPipelineId, urlParams = {} }) => ({
        url: `organizations/${orgId}/tenants/${tenantId}/nlp-pipelines/${nlpPipelineId}/status`,
        method: 'GET',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: false,
          messages: MESSAGES.FETCH_NLP_PIPELINE_STATUS.ERROR
        }
      })
    }),

    createNlpPipeline: builder.mutation({
      query: ({ orgId, tenantId, data, urlParams = {}, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/tenants/${tenantId}/nlp-pipelines`,
        method: 'POST',
        params: {
          ...urlParams
        },
        body: data,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.CREATE_NLP_PIPELINE.ERROR
        }
      }),
      invalidatesTags: (result, error, args) => {
        if (error) {
          return;
        }
        return [
          { type: API_TAGS.NLP_PIPELINES, id: 'LIST' },
          { type: API_TAGS.BOTS, id: args.tenantId }
        ];
      }
    }),

    updateNlpPipeline: builder.mutation({
      query: ({ orgId, tenantId, nlpPipelineId, data, urlParams = {}, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/tenants/${tenantId}/nlp-pipelines/${nlpPipelineId}`,
        method: 'PATCH',
        params: {
          ...urlParams
        },
        body: data,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.UPDATE_NLP_PIPELINE.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.UPDATE_NLP_PIPELINE.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      invalidatesTags: (result, error, args) => {
        if (error) {
          return;
        }
        return [
          { type: API_TAGS.NLP_PIPELINES, id: args.nlpPipelineId },
          { type: API_TAGS.BOTS, id: args.tenantId }
        ];
      }
    }),

    deleteNlpPipeline: builder.mutation({
      query: ({ orgId, tenantId, nlpPipelineId, urlParams = {}, successMessage = { showMessage: true } }) => ({
        url: `organizations/${orgId}/tenants/${tenantId}/nlp-pipelines/${nlpPipelineId}`,
        method: 'DELETE',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.DELETE_NLP_PIPELINE.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.DELETE_NLP_PIPELINE.SUCCESS
        }
      }),
      invalidatesTags: (result, error, args) => {
        if (error) {
          return;
        }
        return [
          { type: API_TAGS.NLP_PIPELINES, id: 'LIST' },
          { type: API_TAGS.BOTS, id: args.tenantId }
        ];
      }
    }),

    trainNlpPipeline: builder.mutation({
      query: ({ orgId, tenantId, nlpPipelineId, urlParams, successMessage = { showMessage: true }, apiUrl = API_CORE_URL }) => ({
        url: `organizations/${orgId}/tenants/${tenantId}/nlp-pipelines/${nlpPipelineId}/train`,
        API_URL: apiUrl,
        method: 'POST',
        params: {
          ...urlParams
        },
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.TRAIN_NLP_PIPELINE.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.TRAIN_NLP_PIPELINE.SUCCESS
        }
      }),
      transformResponse: (response) => true,
      transformErrorResponse: (response) => false,
      invalidatesTags: (result, error, args) => {
        if (error) {
          return;
        }
        return [{ type: API_TAGS.BOTS, id: args.tenantId }];
      }
    }),

    fetchHelviaPipelineSettings: builder.query({
      query: ({ serviceUrl, pipelineId, token, urlParams = {} }) => ({
        url: `/pipelines/${pipelineId}`,
        method: 'GET',
        params: {
          ...urlParams
        },
        API_URL: serviceUrl,
        errorMessage: {
          showMessage: false,
          messages: MESSAGES.FETCH_HELVIA_PIPELINE_SETTINGS.ERROR
        },
        extraToken: token
      }),
      providesTags: (result, error, args) => {
        return [
          { type: API_TAGS.HELVIA_PIPELINE_SETTINGS, id: args.pipelineId }
        ];
      }
    }),

    createHelviaPipelineSettings: builder.mutation({
      query: ({ serviceUrl, data, token, urlParams = {}, successMessage = { showMessage: true } }) => ({
        url: `/pipelines/`,
        method: 'POST',
        params: {
          ...urlParams
        },
        body: data,
        API_URL: serviceUrl,
        extraToken: token,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.CREATE_HELVIA_PIPELINE_SETTINGS.ERROR
        }
      }),
      invalidatesTags: (result, error, args) => {
        if (error) {
          return;
        }
        return [
          { type: API_TAGS.NLP_PIPELINES, id: 'LIST' }
        ];
      }
    }),

    updateHelviaPipelineSettings: builder.mutation({
      query: ({ serviceUrl, pipelineId, token, data, urlParams = {}, successMessage = { showMessage: true } }) => ({
        url: `/pipelines/${pipelineId}`,
        method: 'PATCH',
        params: {
          ...urlParams
        },
        body: data,
        API_URL: serviceUrl,
        extraToken: token,
        errorMessage: {
          showMessage: true,
          messages: MESSAGES.UPDATE_HELVIA_PIPELINE_SETTINGS.ERROR
        },
        successMessage: {
          ...successMessage,
          messages: MESSAGES.UPDATE_HELVIA_PIPELINE_SETTINGS.SUCCESS
        }
      }),
      invalidatesTags: (result, error, args) => {
        if (error) {
          return;
        }
        return [
          { type: API_TAGS.HELVIA_PIPELINE_SETTINGS, id: args.pipelineId }
        ];
      }
    })
  })
});

export const {
  useFetchNlpPipelinesQuery,
  useLazyFetchNlpPipelinesQuery,
  useFetchNlpPipelineQuery,
  useFetchNlpPipelineStatusQuery,
  useCreateNlpPipelineMutation,
  useUpdateNlpPipelineMutation,
  useDeleteNlpPipelineMutation,
  useTrainNlpPipelineMutation,
  useFetchHelviaPipelineSettingsQuery,
  useLazyFetchHelviaPipelineSettingsQuery,
  useCreateHelviaPipelineSettingsMutation,
  useUpdateHelviaPipelineSettingsMutation
} = nlpPipelinesSlice;
